/**
 * The `fields=` projection for the sent-mails table — ONE list, used by the
 * page's first-page seed and by the provider's every refetch.
 *
 * Audit 2026-09-12. There used to be two: the page asked for 49 fields, the
 * provider's refetch hardcoded 19, and the page's own docblock claimed they
 * were "in step". They were not, and the failure was invisible on first paint:
 * page 1 rendered rich, then the first sort / search / filter / page step
 * refetched the narrow list and 28 columns went blank — including
 * `additionalFields`, i.e. every custom-field column — and stayed blank,
 * because every later request was narrow too. Only a customised view was hit,
 * which is why it survived: the default columns happen to be in both lists.
 *
 * Fields the backend's allow-lists drop are deliberately ABSENT — asking for
 * them costs a query parameter and buys a column that can never fill:
 *   `status`        — not a field on this DTO at all (no label in any locale)
 *   `imageUrl`      — guest avatar, never projected by the email-sent lane
 *   `numCompanions` / `companions` — mapped on the DTO, never projected
 *   `senderMailId`  — projected, but the Java entity never learned the field,
 *                     so the mapper has no arm for it and it reads null forever
 * All four that had a column label were removed from `FIELDS_EMAIL_SENT` in
 * the same change; this list and that catalogue are pinned against each other
 * by `sentMailsFieldParity.test.ts`.
 *
 * `html`, `text` and `attachments` stay out for the original reason: nothing on
 * the row reads them (the chip reads the boolean `attachmentsDegraded`, the
 * detail popup fetches the body per mail), and they are the heaviest fields on
 * the document.
 */
export const SENT_MAILS_LIST_FIELDS =
  "emailStatus,skipReason,attachmentsDegraded,softBounces,sendName,sendClass,notificationContactId,templateName,recipientMail,sentAt,emailSentId,mailSubject,guestId,firstName,lastName,eventId,guestType,mainGuest,state,acceptanceState,gender,title,company,jobTitle,address,address2,zipCode,archived,city,country,email,phone,mobile,language,username,code,checkedIn,checkedInAt,createdAt,updatedAt,emailOptIn,additionalFields,senderMail,replyToMail,ccMail,bccMail,humanClickAt,machineClickAt,portalVisitedViaMailAt,websiteVisitedViaMailAt,websiteLoginAt,portalVisitedAt";

/**
 * The `fields=` projection for the sent-mail DETAIL popup (`GET
 * /email-sent/{eventId}/{emailSentId}`), the sibling of the list above.
 *
 * Same trap, one lane over: main-backend projects the row down to exactly
 * these names and drops the rest without a word, so a field missing HERE
 * never reaches the panel and the panel cannot tell "absent" from "not
 * asked for". Two of these were lost that way before they were pinned —
 * `trackingMode` / `trackingModeRequested` (2026-09-18) and `skipReason`
 * (2026-09-24: the row said SKIPPED, the dialog said nothing, and the only
 * copy of the reason sat unrequested on the server). `sendClass` /
 * `notificationContactId` (W4-3, 2026-10-18): a STAFF_NOTICE row renders
 * "Notification to contact" instead of a guest recipient — without the two
 * the panel would show a staff address as if it were the guest's.
 *
 * `failReason` (board H-14, 2026-10-30): a FAILED row has no SES message id
 * and so no events — without the code the dialog had nothing to say at all.
 *
 * `emailTemplateId` stays: the backend resolves a blank `templateName` from
 * `email_templates` through it (SKIPPED rows never store the name).
 */
export const SENT_MAIL_DETAIL_FIELDS =
  "subject,eventData,tracking,tags,configurationSet,timestamp,eventType,source,destination,emailSentId,eventId,guestId,emailTemplateId,senderMail,recipientMail,html,text,attachments,replyToMail,bccMail,ccMail,mailSubject,awsMessageId,sentAt,templateName,emailStatus,skipReason,failReason,attachmentsDegraded,attachmentDrops,attachmentDegraded,softBounces,trackingMode,trackingModeRequested,sendClass,notificationContactId,notificationContactLabel,humanClickAt,machineClickAt,portalVisitedViaMailAt,websiteVisitedViaMailAt";
